import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

/** Batas diam sebelum sesi ditutup otomatis (menit). */
export const IDLE_MENIT = 10;

const IDLE_MS = IDLE_MENIT * 60 * 1000;
const PERIKSA_MS = 5 * 1000;          // seberapa sering kondisi diam diperiksa
const CATAT_MIN_JEDA_MS = 1000;       // batasi penulisan agar tidak tiap piksel gerak

// Disimpan di localStorage supaya aktivitas di satu tab ikut menjaga tab lain
// tetap hidup, dan tetap terbaca meski tab sempat ditutup lalu dibuka lagi.
export const KUNCI_AKTIVITAS = "kedan_last_activity";
export const KUNCI_ALASAN_KELUAR = "kedan_logout_reason";

const PERISTIWA = [
  "mousedown", "mousemove", "wheel", "keydown",
  "touchstart", "touchmove", "scroll", "click", "focus",
];

const sekarang = () => Date.now();

const bacaAktivitas = () => {
  try {
    return Number(localStorage.getItem(KUNCI_AKTIVITAS)) || 0;
  } catch {
    return 0;
  }
};

const tulisAktivitas = (waktu) => {
  try {
    localStorage.setItem(KUNCI_AKTIVITAS, String(waktu));
  } catch {
    // Mode privat / storage diblokir: timer tetap jalan dari memori.
  }
};

/**
 * Menutup sesi otomatis bila tidak ada aktivitas pengguna selama IDLE_MENIT.
 *
 * Aktivitas dihitung dari interaksi nyata (gerak tetikus, ketikan, sentuhan,
 * gulir). Permintaan latar seperti polling notifikasi sengaja tidak dihitung,
 * karena itu bukan tanda pengguna masih di depan layar.
 *
 * @param {boolean} aktif jalankan pemantauan (mis. hanya saat sudah login)
 */
export default function useIdleLogout(aktif) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const terakhirRef = useRef(sekarang());
  const sedangKeluarRef = useRef(false);

  const keluar = useCallback(async () => {
    if (sedangKeluarRef.current) return;
    sedangKeluarRef.current = true;
    try {
      localStorage.setItem(KUNCI_ALASAN_KELUAR, "idle");
      localStorage.removeItem(KUNCI_AKTIVITAS);
    } catch {
      // abaikan
    }
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  }, [logout, navigate]);

  useEffect(() => {
    if (!aktif) return undefined;

    const awal = sekarang();
    terakhirRef.current = awal;
    tulisAktivitas(awal);

    const catat = () => {
      const kini = sekarang();
      if (kini - terakhirRef.current < CATAT_MIN_JEDA_MS) return;
      terakhirRef.current = kini;
      tulisAktivitas(kini);
    };

    PERISTIWA.forEach((p) => window.addEventListener(p, catat, { passive: true }));

    const periksa = () => {
      // Ambil nilai terbaru antar-tab; kalau storage tidak tersedia pakai memori.
      const terakhir = Math.max(terakhirRef.current, bacaAktivitas());
      if (sekarang() - terakhir >= IDLE_MS) keluar();
    };

    const timer = setInterval(periksa, PERIKSA_MS);
    // Saat tab kembali dibuka, jangan menunggu siklus berikutnya.
    document.addEventListener("visibilitychange", periksa);

    return () => {
      PERISTIWA.forEach((p) => window.removeEventListener(p, catat));
      document.removeEventListener("visibilitychange", periksa);
      clearInterval(timer);
    };
  }, [aktif, keluar]);
}

/** Ambil & bersihkan alasan keluar terakhir (dipakai halaman login). */
export function ambilAlasanKeluar() {
  try {
    const alasan = localStorage.getItem(KUNCI_ALASAN_KELUAR);
    if (alasan) localStorage.removeItem(KUNCI_ALASAN_KELUAR);
    return alasan;
  } catch {
    return null;
  }
}
